/**
 * Feature Form Dialog
 *
 * Modal dialog for creating or editing a devcontainer feature.
 */

import { useState, useEffect, type ReactElement, type FormEvent } from 'react';
import type { Feature } from '../../schemas/admin.schemas';
import type { OptionSchemaEntry } from '../../schemas/feature.schemas';

type FeatureFormData = Omit<
  Feature,
  'id' | 'created_at' | 'updated_at' | 'is_active'
>;

const EMPTY_FORM: FeatureFormData = {
  name: '',
  display_name: '',
  description: '',
  source: '',
  version: '',
  default_options: {},
  option_schema: {},
  category: 'languages',
  is_gated: false,
  gate_attribute: null,
  gate_value: null,
  dependencies: [],
  dependencies_note: null,
};

const CATEGORIES = ['languages', 'cloud', 'ai-tool', 'tooling'];

interface FeatureFormDialogProps {
  open: boolean;
  feature: Feature | null;
  onClose: () => void;
  onSubmit: (data: FeatureFormData) => void;
  isSubmitting: boolean;
}

export function FeatureFormDialog({
  open,
  feature,
  onClose,
  onSubmit,
  isSubmitting,
}: FeatureFormDialogProps): ReactElement | null {
  const [form, setForm] = useState<FeatureFormData>(EMPTY_FORM);
  const [defaultOptionsJson, setDefaultOptionsJson] = useState('{}');
  const [optionSchemaJson, setOptionSchemaJson] = useState('{}');
  const [jsonError, setJsonError] = useState<string | null>(null);

  const isEdit = feature !== null;

  useEffect(() => {
    if (feature) {
      setForm({
        name: feature.name,
        display_name: feature.display_name,
        description: feature.description,
        source: feature.source,
        version: feature.version,
        default_options: feature.default_options,
        option_schema: feature.option_schema,
        category: feature.category,
        is_gated: feature.is_gated,
        gate_attribute: feature.gate_attribute,
        gate_value: feature.gate_value,
        dependencies: feature.dependencies,
        dependencies_note: feature.dependencies_note ?? null,
      });
      setDefaultOptionsJson(JSON.stringify(feature.default_options, null, 2));
      setOptionSchemaJson(JSON.stringify(feature.option_schema, null, 2));
    } else {
      setForm(EMPTY_FORM);
      setDefaultOptionsJson('{}');
      setOptionSchemaJson('{}');
    }
    setJsonError(null);
  }, [feature, open]);

  if (!open) return null;

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setJsonError(null);
    try {
      const opts = JSON.parse(defaultOptionsJson) as Record<string, unknown>;
      const schema = JSON.parse(optionSchemaJson) as Record<string, OptionSchemaEntry>;
      onSubmit({ ...form, default_options: opts, option_schema: schema });
    } catch {
      setJsonError('Invalid JSON in options or schema');
    }
  };

  const setField = <K extends keyof FeatureFormData>(
    key: K,
    value: FeatureFormData[K],
  ): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="dialog-overlay" onClick={onClose} role="presentation">
      <dialog
        className="dialog"
        open
        onClick={(e) => { e.stopPropagation(); }}
        aria-label={isEdit ? 'Edit feature' : 'Create feature'}
      >
        <h2>{isEdit ? 'Edit Feature' : 'Create Feature'}</h2>
        <form onSubmit={handleSubmit} className="dialog__form">
          <label className="dialog__field">
            Name
            <input
              value={form.name}
              onChange={(e) => { setField('name', e.target.value); }}
              required
              {...(isEdit ? { disabled: true } : {})}
            />
          </label>
          <label className="dialog__field">
            Display Name
            <input
              value={form.display_name}
              onChange={(e) => { setField('display_name', e.target.value); }}
              required
            />
          </label>
          <label className="dialog__field">
            Description
            <textarea
              value={form.description}
              onChange={(e) => { setField('description', e.target.value); }}
              rows={2}
            />
          </label>
          <label className="dialog__field">
            Source
            <input
              value={form.source}
              onChange={(e) => { setField('source', e.target.value); }}
              required
            />
          </label>
          <label className="dialog__field">
            Version
            <input
              value={form.version}
              onChange={(e) => { setField('version', e.target.value); }}
              required
            />
          </label>
          <label className="dialog__field">
            Category
            <select
              value={form.category}
              onChange={(e) => { setField('category', e.target.value); }}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="dialog__field dialog__field--checkbox">
            <input
              type="checkbox"
              checked={form.is_gated}
              onChange={(e) => { setField('is_gated', e.target.checked); }}
            />
            Gated feature
          </label>
          {form.is_gated && (
            <>
              <label className="dialog__field">
                Gate Attribute (JWT claim path)
                <input
                  value={form.gate_attribute ?? ''}
                  onChange={(e) =>
                    { setField('gate_attribute', e.target.value || null); }
                  }
                />
              </label>
              <label className="dialog__field">
                Gate Value
                <input
                  value={form.gate_value ?? ''}
                  onChange={(e) =>
                    { setField('gate_value', e.target.value || null); }
                  }
                />
              </label>
            </>
          )}
          <label className="dialog__field">
            Default Options (JSON)
            <textarea
              value={defaultOptionsJson}
              onChange={(e) => { setDefaultOptionsJson(e.target.value); }}
              rows={3}
              className="dialog__json"
            />
          </label>
          <label className="dialog__field">
            Option Schema (JSON)
            <textarea
              value={optionSchemaJson}
              onChange={(e) => { setOptionSchemaJson(e.target.value); }}
              rows={3}
              className="dialog__json"
            />
          </label>
          {jsonError && (
            <div className="dialog__error" role="alert">
              {jsonError}
            </div>
          )}
          <div className="dialog__actions">
            <button
              type="button"
              onClick={onClose}
              className="dialog__btn dialog__btn--secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="dialog__btn dialog__btn--primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </dialog>
    </div>
  );
}
